import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Typography } from '@mui/material';
import { billingApi } from './api';
import { useBillingAccess } from './BillingAccess';

export default function CaseEnrollmentControl({ caseId, initial, onChanged, isPlaceholder }) {
  const access = useBillingAccess();
  const allowed = access?.status.can_manage_billing === true;
  const [enrollment, setEnrollment] = useState(initial ?? null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const sequence = useRef(0);
  const invalidate = useCallback(() => { sequence.current++; }, []);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    try { const value = await billingApi.enrollment(caseId); if (request === sequence.current) setEnrollment(value); }
    catch (failure) { if (request === sequence.current) { setEnrollment(null); setError(failure.message); } }
  }, [caseId]);
  useEffect(() => {
    setError(''); setConfirm(false); setEnrollment(initial ?? null);
    if (allowed && !initial) load();
    return invalidate;
  }, [caseId, initial, allowed, load, invalidate, isPlaceholder]);
  if (!allowed) return null;
  if (isPlaceholder || enrollment?.is_placeholder) return <Typography variant="body2">Awaiting case setup · Not billed</Typography>;
  const update = async () => {
    if (!enrollment || enrollment.case_id !== caseId || inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const next = await billingApi.setEnrollment(caseId, { subscribed: !enrollment.subscribed, expected_period_id: enrollment.period_id });
      setEnrollment(next); setConfirm(false);
      await onChanged?.();
      await access.refreshStatus();
    } catch (failure) { setConfirm(false); setError(failure.message); await load(); }
    finally { inFlight.current = false; setBusy(false); }
  };
  return <Box>
    {error && <Alert severity="error" action={<Button onClick={() => { setError(''); load(); }}>Refresh</Button>}>{error}</Alert>}
    <FormControlLabel control={<Switch checked={enrollment?.subscribed ?? false} disabled={busy || !enrollment || enrollment.case_id !== caseId}
      onChange={() => setConfirm(true)} inputProps={{ 'aria-label': `Case ${caseId} billing` }} />}
      label={enrollment ? enrollment.subscribed ? 'Subscribed' : 'Not subscribed' : 'Loading billing…'} />
    <Dialog open={confirm} onClose={() => !busy && setConfirm(false)} aria-labelledby={`case-billing-${caseId}`}>
      <DialogTitle id={`case-billing-${caseId}`}>{enrollment?.subscribed ? 'Remove case from billing?' : 'Add case to billing?'}</DialogTitle>
      <DialogContent><Typography>{enrollment?.subscribed
        ? 'Billing stops immediately. The case becomes hidden from the client, while staff retain its history. Charges already accrued remain on the monthly bill.'
        : 'Billing starts immediately at your firm’s current plan rate, prorated for time subscribed. The client can access the case again.'}</Typography></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setConfirm(false)}>Keep unchanged</Button>
        <Button variant="contained" disabled={busy} onClick={update}>{busy ? 'Saving…' : 'Confirm change'}</Button></DialogActions>
    </Dialog>
  </Box>;
}
